import React, { useState } from 'react';
import { workersAI } from '../services/workersAI';
import { useAnalytics } from '../utils/analytics';

interface DemoStep {
  step: number;
  title: string;
  description: string;
  icon: string;
  time: string;
}

export const LandingHero: React.FC = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [demoResult, setDemoResult] = useState<any>(null);
  const analytics = useAnalytics();

  const steps: DemoStep[] = [
    {
      step: 1,
      title: "AI Writes Viral Hooks",
      description: "Generate scroll-stopping ideas that trigger curiosity and urgency",
      icon: "💡",
      time: "5s"
    },
    {
      step: 2, 
      title: "Creates Professional Script",
      description: "3-beat structure: Hook → Value → Call-to-Action with psychological triggers",
      icon: "📝",
      time: "8s"
    },
    {
      step: 3,
      title: "Plans Perfect Shots",
      description: "Detailed storyboard with visual directions and asset requirements",
      icon: "🎬",
      time: "12s"
    },
    {
      step: 4,
      title: "Generates Eye-Catching Thumbnail",
      description: "Professional cover image with your brand colors and style",
      icon: "🖼️",
      time: "15s"
    },
    {
      step: 5,
      title: "Creates Studio-Quality Voiceover",
      description: "Choose from 12+ professional voices with natural intonation",
      icon: "🎵",
      time: "10s"
    }
  ];

  const runQuickDemo = async () => {
    setIsGenerating(true);
    setCurrentStep(0);
    setDemoResult(null);

    // Track demo start
    analytics.trackDemoStart('live_demo', 'hero_cta');
    analytics.trackCTAClick('Try Live Demo', 'hero_section');

    try {
      const demoTopic = "productivity tips that actually work";
      
      // Step 1: Ideas
      setCurrentStep(1);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate progress
      
      const ideas = await workersAI.generateIdeas({
        topic: demoTopic,
        language: 'en',
        count: 3
      });
      
      const selectedHook = ideas.ideas[0];
      
      // Step 2: Script  
      setCurrentStep(2);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const script = await workersAI.generateScript({
        hook: selectedHook,
        topic: demoTopic,
        language: 'en',
        duration: 45
      });
      
      // Step 3: Storyboard
      setCurrentStep(3);
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const storyboard = await workersAI.generateStoryboard({
        script: script.script,
        duration: 45,
        language: 'en',
        visualStyle: 'modern',
        beats: script.beats
      });
      
      // Step 4: Thumbnail
      setCurrentStep(4);
      await new Promise(resolve => setTimeout(resolve, 1800));
      
      const thumbnail = await workersAI.generateThumbnail({
        prompt: `${selectedHook} - ${demoTopic}`,
        style: 'professional',
        format: 'landscape',
        brandColors: { primary: '#007bff', secondary: '#28a745' }
      });
      
      // Step 5: TTS
      setCurrentStep(5);
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      const tts = await workersAI.generateTTS({
        text: script.script.substring(0, 200) + "...", // Shorter for demo
        voice: 'asteria',
        language: 'en'
      });
      
      const result = {
        hook: selectedHook,
        script: script.script,
        thumbnail: thumbnail.imageUrl,
        audio: tts.audioUrl,
        storyboard: storyboard.storyboard,
        stats: {
          totalTime: "~3 minutes",
          wordCount: script.wordCount,
          shots: storyboard.storyboard.length,
          voice: tts.voice
        }
      };

      setDemoResult(result);

      // Track successful demo completion
      analytics.trackDemoComplete('live_demo', 180, result); // Approximate 3 minutes
      analytics.trackConversion({
        type: 'demo_completed',
        source: 'hero_live_demo',
        properties: {
          generatedHook: selectedHook,
          wordCount: script.wordCount,
          shots: storyboard.storyboard.length,
          voice: tts.voice
        }
      });
      
    } catch (error) {
      console.error('Demo failed:', error);
      analytics.trackFeatureInteraction('live_demo', 'error', { error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      setIsGenerating(false);
      setCurrentStep(0);
    }
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-black opacity-50"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center">
          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            Turn Ideas Into
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              {" "}Viral Reels
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-4 max-w-4xl mx-auto">
            AI-powered reel generation in under <strong className="text-yellow-400">5 minutes</strong>
          </p>
          
          <p className="text-lg text-gray-300 mb-12 max-w-3xl mx-auto">
            Perfect for creators who want professional reels without filming, editing, or appearing on camera. 
            Upload your idea → Get a complete reel with script, voiceover, and thumbnail.
          </p>

          {/* Value Props */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl mb-2">🚀</div>
              <h3 className="text-white font-semibold mb-1">5-Minute Generation</h3>
              <p className="text-gray-300 text-sm">From idea to complete reel faster than manual editing</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl mb-2">🎭</div>
              <h3 className="text-white font-semibold mb-1">Faceless Friendly</h3>
              <p className="text-gray-300 text-sm">Perfect for creators who prefer voiceover content</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="text-2xl mb-2">📱</div>
              <h3 className="text-white font-semibold mb-1">Platform Ready</h3>
              <p className="text-gray-300 text-sm">Optimized for Instagram, TikTok, and YouTube Shorts</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button
              onClick={runQuickDemo}
              disabled={isGenerating}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-8 py-4 rounded-lg font-bold text-lg hover:from-yellow-300 hover:to-orange-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl"
            >
              {isGenerating ? `Generating... Step ${currentStep}/5` : '🎬 Try Live Demo'}
            </button>
            <button 
              onClick={() => {
                analytics.trackCTAClick('Watch Examples', 'hero_section');
                analytics.trackFeatureInteraction('video_examples', 'click');
              }}
              className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/30 transition-all border border-white/30"
            >
              📹 Watch Examples
            </button>
          </div>
        </div>

        {/* Process Visualization */}
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            How AutoReelr Creates Your Reel
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <div 
                key={step.step}
                className={`relative bg-white/10 backdrop-blur-sm rounded-lg p-6 border-2 transition-all duration-500 ${
                  isGenerating && currentStep >= step.step 
                    ? 'border-yellow-400 bg-yellow-400/20 scale-105' 
                    : 'border-white/20'
                }`}
              >
                {/* Step Number */}
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-black font-bold text-sm">
                  {step.step}
                </div>
                
                {/* Icon */}
                <div className="text-4xl mb-4 text-center">{step.icon}</div>
                
                {/* Content */}
                <h3 className="text-white font-bold text-lg mb-2 text-center">{step.title}</h3>
                <p className="text-gray-300 text-sm text-center mb-4">{step.description}</p>
                
                {/* Time */}
                <div className="text-center">
                  <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    ~{step.time}
                  </span>
                </div>
                
                {/* Progress Indicator */}
                {isGenerating && currentStep >= step.step && (
                  <div className="absolute inset-0 flex items-center justify-center bg-yellow-400/20 rounded-lg">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
                  </div>
                )}
                
                {/* Arrow */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-white/50">
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Demo Result Display */}
        {demoResult && (
          <div className="mt-16 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              🎉 Your Demo Reel is Ready!
            </h3>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left: Content */}
                <div>
                  <h4 className="text-yellow-400 font-bold text-lg mb-2">Generated Hook:</h4>
                  <p className="text-white mb-4">"{demoResult.hook}"</p>
                  
                  <h4 className="text-yellow-400 font-bold text-lg mb-2">Script Preview:</h4>
                  <p className="text-gray-300 text-sm mb-4 line-clamp-4">
                    {demoResult.script.substring(0, 200)}...
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white/10 rounded p-3">
                      <div className="text-yellow-400 font-semibold">Generation Time</div>
                      <div className="text-white">{demoResult.stats.totalTime}</div>
                    </div>
                    <div className="bg-white/10 rounded p-3">
                      <div className="text-yellow-400 font-semibold">Word Count</div>
                      <div className="text-white">{demoResult.stats.wordCount} words</div>
                    </div>
                    <div className="bg-white/10 rounded p-3">
                      <div className="text-yellow-400 font-semibold">Shots Planned</div>
                      <div className="text-white">{demoResult.stats.shots} shots</div>
                    </div>
                    <div className="bg-white/10 rounded p-3">
                      <div className="text-yellow-400 font-semibold">Voice Used</div>
                      <div className="text-white">{demoResult.stats.voice}</div>
                    </div>
                  </div>
                </div>
                
                {/* Right: Media */}
                <div className="space-y-4">
                  {/* Thumbnail */}
                  <div>
                    <h4 className="text-yellow-400 font-bold text-lg mb-2">Generated Thumbnail:</h4>
                    <img 
                      src={demoResult.thumbnail} 
                      alt="Generated thumbnail"
                      className="w-full rounded-lg shadow-lg"
                    />
                  </div>
                  
                  {/* Audio */}
                  <div>
                    <h4 className="text-yellow-400 font-bold text-lg mb-2">Voiceover Preview:</h4>
                    <audio controls className="w-full">
                      <source src={demoResult.audio} type="audio/mpeg" />
                      Your browser does not support audio playback.
                    </audio>
                  </div>
                  
                  {/* CTA */}
                  <div className="pt-4">
                    <button 
                      onClick={() => {
                        analytics.trackCTAClick('Create Your Own Reel', 'demo_results');
                        analytics.trackConversion({
                          type: 'signup_started',
                          source: 'demo_completion_cta',
                          properties: {
                            completedDemo: true,
                            demoResult: demoResult?.stats
                          }
                        });
                      }}
                      className="w-full bg-gradient-to-r from-green-400 to-blue-500 text-white px-6 py-3 rounded-lg font-bold hover:from-green-300 hover:to-blue-400 transition-all"
                    >
                      🚀 Create Your Own Reel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trust Indicators */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-4">Trusted by content creators worldwide</p>
          <div className="flex justify-center items-center space-x-8 text-gray-500">
            <div className="flex items-center">
              <span className="text-yellow-400 mr-2">⚡</span>
              <span>Cloudflare AI Powered</span>
            </div>
            <div className="flex items-center">
              <span className="text-yellow-400 mr-2">🔒</span>
              <span>Privacy Focused</span>
            </div>
            <div className="flex items-center">
              <span className="text-yellow-400 mr-2">🚀</span>
              <span>Industry Leading Speed</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingHero;